import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Gem, ArrowRight, Mail, Lock, User } from "lucide-react";

const AuthShell = ({ children, title, sub }) => (
  <div className="min-h-screen flex">
    {/* Left brand panel */}
    <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-white/5">
      <div className="absolute inset-0" style={{
        background: "radial-gradient(ellipse 60% 50% at 30% 40%, rgba(244,208,63,0.18), transparent 60%), radial-gradient(ellipse 50% 40% at 70% 70%, rgba(91,44,111,0.3), transparent 60%)"
      }} />
      <div className="noise-overlay" />
      <div className="relative flex flex-col justify-between p-12 w-full">
        <Link to="/" className="flex items-center gap-2.5">
          <Gem className="w-7 h-7 text-gold" />
          <span className="font-display text-xl font-semibold text-white">My Leads Convert</span>
        </Link>
        <div>
          <h2 className="font-display text-4xl text-white leading-tight mb-4">
            "The first lead I opened was a real nonprofit with a real 990 on file. That's what sold me."
          </h2>
          <div className="text-sm text-zinc-400">— what an honest early-access user might say. We'll swap this out for a real quote when we have one.</div>
        </div>
        <div className="text-xs text-zinc-600 font-mono">my leads convert · real records · grounded drafts</div>
      </div>
    </div>

    {/* Right form panel */}
    <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
      <div className="w-full max-w-md">
        <div className="lg:hidden mb-8">
          <Link to="/" className="flex items-center gap-2.5">
            <Gem className="w-7 h-7 text-gold" />
            <span className="font-display text-xl font-semibold text-white">My Leads Convert</span>
          </Link>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-white mb-2">{title}</h1>
        <p className="text-zinc-400 mb-8">{sub}</p>
        {children}
      </div>
    </div>
  </div>
);

export const Login = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) nav("/dashboard");
    else setError(res.error);
  };

  return (
    <AuthShell title="Welcome back" sub="Sign in to your My Leads Convert workspace.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="label-eyebrow block mb-2">Email</label>
          <div className="relative">
            <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-zinc-500" />
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
              className="input-glass pl-10" placeholder="you@company.com" data-testid="login-email" />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="label-eyebrow">Password</label>
            <Link to="/forgot-password" className="text-xs text-zinc-400 hover:text-gold transition-colors" data-testid="link-forgot-password">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Lock className="absolute left-3.5 top-3.5 w-4 h-4 text-zinc-500" />
            <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
              className="input-glass pl-10" placeholder="••••••••" data-testid="login-password" />
          </div>
        </div>
        {error && <div className="text-sm text-rose-400" data-testid="login-error">{error}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="login-submit">
          {busy ? "Signing in..." : <>Sign in <ArrowRight className="w-4 h-4" /></>}
        </button>
        <div className="text-sm text-zinc-400 text-center">
          New to My Leads Convert? <Link to="/register" className="text-gold hover:underline" data-testid="link-to-register">Create an account</Link>
        </div>
        <div className="text-xs text-zinc-600 text-center pt-2">
          Try the demo: admin@myleadsconvert.us / admin123
        </div>
      </form>
    </AuthShell>
  );
};

export const Register = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    const res = await register(name, email, password);
    setBusy(false);
    if (res.ok) nav("/onboarding");
    else setError(res.error);
  };

  return (
    <AuthShell title="Create your workspace" sub="Sign up in seconds and start mining real nonprofit leads.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <label className="label-eyebrow block mb-2">Your name</label>
          <div className="relative">
            <User className="absolute left-3.5 top-3.5 w-4 h-4 text-zinc-500" />
            <input required value={name} onChange={e => setName(e.target.value)}
              className="input-glass pl-10" placeholder="Sara Kim" data-testid="register-name" />
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Work email</label>
          <div className="relative">
            <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-zinc-500" />
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
              className="input-glass pl-10" placeholder="you@company.com" data-testid="register-email" />
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Password</label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-3.5 w-4 h-4 text-zinc-500" />
            <input type="password" required minLength={6} value={password} onChange={e => setPassword(e.target.value)}
              className="input-glass pl-10" placeholder="6+ characters" data-testid="register-password" />
          </div>
        </div>
        {error && <div className="text-sm text-rose-400" data-testid="register-error">{error}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="register-submit">
          {busy ? "Creating workspace..." : <>Start mining leads <ArrowRight className="w-4 h-4" /></>}
        </button>
        <div className="text-sm text-zinc-400 text-center">
          Already have an account? <Link to="/login" className="text-gold hover:underline" data-testid="link-to-login">Sign in</Link>
        </div>
      </form>
    </AuthShell>
  );
};
